// src/components/modals/CreateProgramModal.jsx
//
// Status isn't shown or editable here at all — moving a program
// DRAFT -> PENDING ("שלח לאישור גפ״ן") lives in the program card's 3-dot
// menu instead (see ProgramCard/CardMenu in SupplierPrograms.jsx), and
// APPROVED/ACTIVE/SUSPENDED/CLOSED are set by GEFEN/admin on the
// backend. This modal only ever touches name/audience/GEFEN number/
// description.
import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useUser } from '../../contexts/UserContext';
import SpinnerOverlay from '../ui/SpinnerOverlay';

/* ── Shared field label ────────────────────────────────────── */
const FieldLabel = ({ htmlFor, required, children }) => (
  <label
    htmlFor={htmlFor}
    className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 text-right"
  >
    {children} {required && <span className="text-red-400 normal-case">*</span>}
  </label>
);

/* ── Error message ─────────────────────────────────────────── */
const FieldError = ({ msg }) =>
  msg ? (
    <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center gap-1 justify-end">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {msg}
    </p>
  ) : null;

/* ── InputField ────────────────────────────────────────────── */
const InputField = React.forwardRef(function InputField(
  {
    label,
    name,
    type = 'text',
    placeholder,
    value,
    onChange,
    onBlur,
    error,
    required = false,
    dir = 'rtl',
    ...props
  },
  ref
) {
  const [focused, setFocused] = useState(false);

  return (
    <div className="min-w-0">
      <FieldLabel htmlFor={name} required={required}>
        {label}
      </FieldLabel>

      <input
        ref={ref}
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        onFocus={() => setFocused(true)}
        dir={dir}
        placeholder={placeholder}
        className={`block w-full min-w-0 h-10 px-3 text-base sm:text-sm rounded-xl border bg-white text-gray-800 text-right
                    transition-all duration-150 outline-none appearance-none
                    ${
                      error
                        ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200'
                        : focused
                        ? 'border-indigo-400 ring-2 ring-indigo-100'
                        : 'border-gray-200 hover:border-indigo-300'
                    }`}
        {...props}
      />

      <FieldError msg={error} />
    </div>
  );
});

/* ── Validation ────────────────────────────────────────────── */
const schema = Yup.object({
  name: Yup.string()
    .trim()
    .min(3, 'שם קצר מדי')
    .max(150, 'שם ארוך מדי')
    .required('שם התוכנית חובה'),
  targetAudience: Yup.string().max(255, 'טקסט ארוך מדי'),
  gefenProgramNumber: Yup.string().max(100, 'ארוך מדי'),
  description: Yup.string().max(1000, 'עד 1000 תווים'),
});

/* ── Main component ────────────────────────────────────────── */
export default function CreateProgramModal({
  isOpen,
  onClose,
  mode = 'create',
  program = null,
  onSubmit,
}) {
  const { showAlert } = useUser();

  const initialValues = useMemo(
    () => ({
      name: program?.name || '',
      targetAudience: program?.target_audience || program?.targetAudience || '',
      gefenProgramNumber:
        program?.gefen_program_number || program?.gefenProgramNumber || '',
      description: program?.description || '',
    }),
    [program]
  );

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          name: values.name.trim(),
          description: (values.description || '').trim(),
          target_audience: (values.targetAudience || '').trim(),
          gefen_program_number: (values.gefenProgramNumber || '').trim() || null,
        };

        if (!onSubmit) {
          console.warn('CreateProgramModal: no onSubmit handler provided.');
        } else {
          await onSubmit({ payload, mode, id: program?.id });
        }

        showAlert?.(
          mode === 'create' ? 'התוכנית נוצרה בהצלחה!' : 'השינויים נשמרו!',
          'success'
        );
        resetForm();
        onClose?.();
      } catch (err) {
        console.error('Program submit error:', err);
        const msg =
          err?.message ||
          (mode === 'create' ? 'יצירת תוכנית נכשלה.' : 'שמירת שינויים נכשלה.');
        showAlert?.(msg, 'error') || alert(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const firstInputRef = useRef(null);
  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('cpf-modal-open');
    return () => document.body.classList.remove('cpf-modal-open');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      hasAutoFocusedRef.current = false;
      return;
    }

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    let t;
    const isSmallScreen = window.innerWidth < 640;

    if (!hasAutoFocusedRef.current && !isSmallScreen) {
      t = setTimeout(() => {
        firstInputRef.current?.focus();
        hasAutoFocusedRef.current = true;
      }, 100);
    }

    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prev;
      if (t) clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const showErrors = formik.submitCount > 0;
  const fe = (k) => (showErrors ? formik.errors[k] : '');
  const descCount = (formik.values.description || '').length;
  const titleText = mode === 'edit' ? 'עריכת תוכנית גפ״ן' : 'תוכנית גפ״ן חדשה';
  const subtitleText =
    mode === 'edit' ? 'עדכון פרטי התוכנית' : 'הגדרת פרטי התוכנית החינוכית';

  const submitLabel = formik.isSubmitting
    ? mode === 'edit'
      ? 'שומר…'
      : 'יוצר...'
    : mode === 'edit'
    ? 'שמור שינויים'
    : 'יצירת תוכנית';

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]"
        onClick={onClose}
      />

      {formik.isSubmitting && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

      <div
        className="relative z-[1] w-full sm:max-w-2xl bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh] overflow-hidden"
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M12 2l2.5 6.5L21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5L12 2z" />
              </svg>
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">
                {titleText}
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">{subtitleText}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
            aria-label="סגור"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form
          id="create-program-form"
          onSubmit={formik.handleSubmit}
          className="flex-1 overflow-y-auto overscroll-contain"
        >
          <div className="px-5 sm:px-7 py-5 space-y-5">
            {/* Row 1 */}
            <InputField
              ref={firstInputRef}
              label="שם התוכנית"
              name="name"
              placeholder="לדוגמה: חונכות חכמה – תיכון"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={fe('name')}
              required
            />

            {/* Row 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField
                label="קהל יעד"
                name="targetAudience"
                placeholder="לדוגמה: תלמידי תיכון, מגמת מדעים"
                value={formik.values.targetAudience}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fe('targetAudience')}
              />

              <InputField
                label="מספר תוכנית בגפ״ן"
                name="gefenProgramNumber"
                placeholder="יוקצה על ידי גפ״ן עם האישור"
                value={formik.values.gefenProgramNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fe('gefenProgramNumber')}
                dir="ltr"
              />
            </div>

            {/* Description */}
            <div className="min-w-0">
              <FieldLabel htmlFor="description">תיאור התוכנית (אופציונלי)</FieldLabel>
              <div className="relative">
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="ספרו על התוכנית: מטרותיה, בתי הספר הרלוונטיים, מבנה הלמידה..."
                  className={`w-full min-w-0 px-3 py-2.5 text-base sm:text-sm rounded-xl border bg-white text-gray-800 text-right
                              resize-none outline-none transition-all duration-150
                              ${
                                showErrors && formik.errors.description
                                  ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200'
                                  : 'border-gray-200 hover:border-indigo-300 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100'
                              }`}
                />
                <span
                  className={`absolute bottom-2.5 left-3 text-[11px] font-medium ${
                    descCount > 900
                      ? 'text-orange-400'
                      : descCount > 800
                      ? 'text-yellow-400'
                      : 'text-gray-300'
                  }`}
                >
                  {descCount}/1000
                </span>
              </div>
              <FieldError msg={showErrors ? formik.errors.description : ''} />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={formik.isSubmitting}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                       hover:bg-gray-50 hover:border-gray-300 transition-all duration-150
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ביטול
          </button>

          <button
            type="submit"
            form="create-program-form"
            disabled={formik.isSubmitting}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold
                       shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0
                       transition-all duration-150 flex items-center gap-2"
          >
            {formik.isSubmitting && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}

            {!formik.isSubmitting && (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}

            {submitLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}